// src/app/services/upmode-agent-api.ts
import {
  apiFetch,
  AuthExpiredError,
  getStoredToken,
  notifyAuthExpired,
} from "@/api/api-fetch";
import { buildApiUrl } from "@/api/url-route";

// streamChat below cannot use apiFetch because it reads the response body
// as an SSE byte stream. It still routes its token + 401 handling through
// the shared helpers so the empty-bearer footgun and silent-stale-token
// behaviour stay fixed across this service too.
function authHeaders(): Record<string, string> {
  const t = getStoredToken();
  const out: Record<string, string> = { "Content-Type": "application/json" };
  if (t) out.Authorization = `Bearer ${t}`;
  return out;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type PublishDestination = "hashnode" | "wordpress" | "linkedin";

export interface ToolsEnabled {
  read_brand_data: boolean;
  send_email: boolean;
  web_search: boolean;
}

export interface ExampleTurn {
  role: "user" | "assistant";
  content: string;
}

export interface ExampleConversation {
  title: string;
  turns: ExampleTurn[];
}

export interface UpmodeAgent {
  id: number;
  username: string;
  name: string;
  description: string;
  avatar_emoji: string;
  system_prompt: string;
  model: string;
  temperature: number;
  tools_enabled: ToolsEnabled;
  example_conversations: ExampleConversation[];
  brand_name: string | null;
  /** Destinations this agent can publish content to. Empty = no publish. */
  publish_destinations: PublishDestination[];
  is_preset: boolean;
  is_active: boolean;
  /** Hidden system agent (e.g. the sidebar Page Assistant). Never shown in the Agent builder. */
  is_system?: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpmodeConversationSummary {
  id: number;
  agent_id: number | null;
  agent_name: string;
  title: string;
  brand_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContextRef {
  type: "brand_run" | "cognition_run" | "agent_run";
  id: number;
  label: string;
}

export interface UpmodeMessage {
  id: number;
  role: "user" | "assistant" | "tool";
  content: string;
  tool_calls: Array<{ id: string; name: string; input: any }>;
  tool_results: Array<{ tool_use_id: string; name: string; result: any }>;
  metadata: Record<string, any>;
  created_at: string;
}

export interface ConversationDetail {
  conversation: UpmodeConversationSummary & { context_refs: ContextRef[] };
  messages: UpmodeMessage[];
}

// ─── Publish confirm payload ─────────────────────────────────────────────────

export interface PublishConfirmDestination {
  destination: PublishDestination;
  content: Record<string, any>;
}

export interface PublishConfirmResult {
  destination: PublishDestination;
  status: "published" | "failed" | "rejected" | "not_configured" | "inactive";
  external_url?: string;
  external_id?: string;
  error?: string;
}

export interface PublishConfirmResponse {
  status: "published" | "partially_published" | "failed";
  results: PublishConfirmResult[];
}

// ─── SSE event shapes ─────────────────────────────────────────────────────────

export type ChatStreamEvent =
  | { type: "text"; delta: string }
  | { type: "tool_use_start"; id: string; name: string }
  | { type: "tool_result"; id: string; name: string; result: any }
  | { type: "email_confirmation"; tool_use_id: string; to_email: string; subject: string; body: string }
  | {
      type: "publish_confirmation";
      tool_use_id: string;
      destinations: PublishDestination[];
      content: Record<PublishDestination, Record<string, any>>;
      summary: string;
    }
  | { type: "done"; note?: string }
  | { type: "error"; message: string };

// ─── Email recipient allow-list ───────────────────────────────────────────────

export interface BrandMember {
  user_id: number;
  /** Usernames are email addresses, and a member's own address is always
   *  allowed — so this list is part of the effective permission, not just
   *  context. */
  username: string;
  full_name: string;
}

export interface BrandEmailRecipients {
  brand_name: string;
  /** Approving a domain permits every address at it — the usual case. */
  domains: string[];
  /** Individually approved addresses, for one-off exceptions. */
  emails: string[];
  caller_address?: string | null;
}

export interface BrandRecipientsRow {
  /** Normalised name the list is stored under: two spellings of one brand
   *  share a single list, and therefore a single row. */
  brand_key: string;
  brand_name: string;
  aliases: string[];
  approved_domains: string[];
  approved_emails: string[];
  members: BrandMember[];
}

export interface OrphanRecipientsRow {
  brand_key: string;
  approved_domains: string[];
  approved_emails: string[];
}

export interface EmailRecipientsOverview {
  brands: BrandRecipientsRow[];
  /** Lists whose brand no longer exists — surfaced so they can be cleared. */
  orphaned: OrphanRecipientsRow[];
}

// ─── REST calls ───────────────────────────────────────────────────────────────

export const upmodeAgentApi = {
  listAgents: (brandName?: string | null) => {
    const qs = brandName ? `?brand_name=${encodeURIComponent(brandName)}` : "";
    return apiFetch<{ items: UpmodeAgent[] }>(`/upmode-agents${qs}`).then(r => r.items);
  },

  createAgent: (body: Omit<UpmodeAgent, "id" | "username" | "is_preset" | "is_active" | "created_at" | "updated_at">) =>
    apiFetch<UpmodeAgent>("/upmode-agents", { method: "POST", body: JSON.stringify(body) }),

  getAgent: (id: number) => apiFetch<UpmodeAgent>(`/upmode-agents/${id}`),

  /** Idempotently fetch (creating on first call) the hidden Page Assistant
   * agent that powers the in-app sidebar. */
  ensurePageAssistant: () =>
    apiFetch<UpmodeAgent>("/upmode-agents/page-assistant", { method: "POST" }),

  updateAgent: (id: number, body: Partial<UpmodeAgent>) =>
    apiFetch<{ ok: boolean }>(`/upmode-agents/${id}`, { method: "PUT", body: JSON.stringify(body) }),

  deleteAgent: (id: number) =>
    apiFetch<{ ok: boolean }>(`/upmode-agents/${id}`, { method: "DELETE" }),

  listConversations: (limit = 50) =>
    apiFetch<{ items: UpmodeConversationSummary[] }>(`/upmode-conversations?limit=${limit}`)
      .then(r => r.items),

  createConversation: (body: {
    agent_id?: number | null;
    title?: string;
    brand_name?: string | null;
    context_refs?: ContextRef[];
  }) =>
    apiFetch<UpmodeConversationSummary & { context_refs: ContextRef[] }>("/upmode-conversations", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getConversation: (id: number) =>
    apiFetch<ConversationDetail>(`/upmode-conversations/${id}`),

  updateConversation: (id: number, body: { title?: string; context_refs?: ContextRef[] }) =>
    apiFetch<{ ok: boolean }>(`/upmode-conversations/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteConversation: (id: number) =>
    apiFetch<{ ok: boolean }>(`/upmode-conversations/${id}`, { method: "DELETE" }),

  sendEmail: (body: { conversation_id: number; to_email: string; subject: string; body: string }) =>
    apiFetch<{ status: string; to_email?: string; error?: string }>("/upmode-email/send", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Email recipient allow-list ────────────────────────────────────────────
  // Which addresses the platform mail account may write to for a brand. Every
  // brand member's own address is always allowed on top of these.

  /** Admin-only. Every brand, its members, and its approved recipients. */
  emailRecipientsOverview: () =>
    apiFetch<EmailRecipientsOverview>("/upmode-email/recipients-overview"),

  /** One brand's list. Readable by anyone assigned to that brand. */
  getEmailRecipients: (brandName: string) =>
    apiFetch<BrandEmailRecipients>(
      `/upmode-email/recipients?brand_name=${encodeURIComponent(brandName)}`,
    ),

  /** Admin-only. Replaces the brand's rules wholesale. An invalid address, a
   *  malformed domain, or a public webmail domain is rejected with a 400 whose
   *  detail explains which and why. */
  setEmailRecipients: (brandName: string, domains: string[], emails: string[]) =>
    apiFetch<BrandEmailRecipients>("/upmode-email/recipients", {
      method: "PUT",
      body: JSON.stringify({ brand_name: brandName, domains, emails }),
    }),

  // NEW — called when user approves a publish_confirmation card
  confirmPublish: (body: {
    conversation_id: number;
    tool_use_id: string;
    destinations: PublishConfirmDestination[];
  }) =>
    apiFetch<PublishConfirmResponse>("/upmode-publish/confirm", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

// ─── SSE streaming chat ───────────────────────────────────────────────────────

export async function streamChat(
  conversationId: number,
  message: string,
  onEvent: (evt: ChatStreamEvent) => void,
  abortSignal?: AbortSignal,
  extraContext?: Record<string, unknown> | null,
): Promise<void> {
  const base = buildApiUrl();
  const res = await fetch(`${base}/upmode-chat`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      message,
      extra_context: extraContext ?? null,
    }),
    signal: abortSignal,
  });

  if (res.status === 401) {
    notifyAuthExpired("/upmode-chat");
    throw new AuthExpiredError("/upmode-chat");
  }
  if (!res.ok) {
    // Mirror api-fetch error hygiene: 5xx / non-JSON bodies are server
    // internals; log them and surface a generic message only.
    const raw = await res.text().catch(() => "");
    let detail = "";
    if (res.status < 500) {
      try { detail = JSON.parse(raw)?.detail; } catch { /* non-JSON body */ }
    }
    if (typeof detail !== "string" || !detail) {
      console.error(`Chat request failed (${res.status}):`, raw);
    }
    throw new Error(
      typeof detail === "string" && detail ? detail : "Chat failed. Please try again.",
    );
  }
  if (!res.body) throw new Error("No response body");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";

    for (const part of parts) {
      const line = part.split("\n").find(l => l.startsWith("data:"));
      if (!line) continue;
      const jsonStr = line.slice(5).trim();
      if (!jsonStr) continue;
      try {
        const evt = JSON.parse(jsonStr) as ChatStreamEvent;
        onEvent(evt);
      } catch {
        // ignore malformed chunk
      }
    }
  }
}